import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FEEDBACK_OPTIONS, feedbackOptionsFor, DOC_TYPE_LONG, DOC_TYPE_CLS, fmtDate } from "@/lib/helpers";
import { DateQuickPick } from "@/components/DateQuickPick";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

const COLOR = {
  green: "bg-green-50 text-green-800 border-green-200 active:bg-green-100",
  red: "bg-red-50 text-red-800 border-red-200 active:bg-red-100",
  amber: "bg-orange-50 text-orange-800 border-orange-200 active:bg-orange-100",
  blue: "bg-accent-50 text-accent-800 border-accent-200 active:bg-accent-100",
};
const ICON_BG = { green: "bg-green-500", red: "bg-red-500", amber: "bg-orange-500", blue: "bg-accent-500" };

export default function Feedback() {
  const { rid } = useParams();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [selected, setSelected] = useState(null);
  const [contact, setContact] = useState("");
  const [comment, setComment] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [workflows, setWorkflows] = useState(null);

  useEffect(() => { api.get(`/requests/${rid}`).then((r) => setReq(r.data)).catch(() => {}); api.get("/document-workflows").then((r) => setWorkflows(r.data)).catch(() => {}); }, [rid]);
  const docType = req?.document_type || "pmt";
  const options = req ? feedbackOptionsFor(workflows, docType) : FEEDBACK_OPTIONS;

  const dateRequired = selected?.result === "revenir_plus_tard";

  const submit = async (opt) => {
    if (opt.result === "revenir_plus_tard" && !nextDate) { toast.error("Une date de récupération est obligatoire."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/requests/${rid}/feedback`, {
        result: opt.result, comment: comment || null, contact_person: contact || null, next_date: nextDate || null,
      });
      if (data.closed) toast.success(`${opt.label} · dossier clôturé${data.points_awarded ? ` · +${data.points_awarded} pt` : ""} ✅`);
      if (data.closed && data.establishment_id && data.remaining_in_establishment === 0) toast.success("Établissement à jour — merci à l'équipe", { duration: 5000 });
      else if (data.points_awarded) toast.success(`Retour enregistré · +${data.points_awarded} pt`);
      else toast.success("Retour enregistré");
      window.dispatchEvent(new CustomEvent("sanitdocs:points-updated"));
      // Retour automatique : établissement s'il reste des documents, sinon liste générale. `replace` = jamais de retour sur une fiche clôturée.
      const dest = data.closed && data.establishment_id && data.remaining_in_establishment > 0
        ? `/driver/etablissement/${data.establishment_id}` : "/driver/a-recuperer";
      nav(dest, { replace: true });
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); setSaving(false); }
  };

  const onSelect = (opt) => { if (!opt.extra) { submit(opt); return; } setSelected(opt); };

  return (
    <div className="space-y-5 pb-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-brand-900">Que s'est-il passé ?</h1>
        {req && <div className="mt-2 flex items-center gap-2 flex-wrap"><span data-testid="feedback-doc-type" className={`text-xs font-bold px-2.5 py-1 rounded-full ${DOC_TYPE_CLS[docType] || DOC_TYPE_CLS.autre}`}>{DOC_TYPE_LONG[docType] || DOC_TYPE_LONG.autre}</span><p className="text-slate-500">{req.patient_name} · {req.establishment_name}</p></div>}
        {req && <p data-testid="feedback-dob" className="text-sm text-slate-500 mt-1">{req.patient_birth_date ? `Né(e) le ${fmtDate(req.patient_birth_date)}` : "Date de naissance non renseignée"}</p>}
        {req?.transport_date && <p data-testid="feedback-transport" className="text-sm text-brand-900 font-semibold mt-1">Transport du {fmtDate(req.transport_date)}{req.transport_time ? ` à ${req.transport_time}` : ""}</p>}
        {req?.instruction && <p data-testid="feedback-instruction" className="mt-2 text-sm bg-orange-50 border border-orange-200 text-orange-900 rounded-lg px-3 py-2 font-semibold">{req.instruction}</p>}
      </div>

      {!selected && (
        <div className="grid grid-cols-1 gap-3">
          {options.map((opt) => (
            <button key={opt.result} data-testid={`feedback-${opt.result}`} disabled={saving} onClick={() => onSelect(opt)}
              className={`w-full min-h-[68px] rounded-2xl border-2 text-xl font-heading font-bold flex items-center gap-4 px-5 active:scale-[0.98] transition-transform ${COLOR[opt.color]}`}>
              <span className={`w-12 h-12 rounded-xl ${ICON_BG[opt.color]} text-white flex items-center justify-center shrink-0`}><opt.icon className="w-7 h-7" /></span>{opt.label}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm animate-fade-in space-y-4">
          <div className="flex items-center gap-3 text-xl font-heading font-bold text-brand-900">
            <span className={`w-10 h-10 rounded-xl ${ICON_BG[selected.color]} text-white flex items-center justify-center`}><selected.icon className="w-6 h-6" /></span>{selected.label}
          </div>

          {selected.extra === "contact" && (
            <div>
              <label className="text-sm font-semibold text-slate-600">Demander à… (médecin, secrétariat, service)</label>
              <Input data-testid="feedback-contact" autoFocus value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Ex : Dr Leroy" className="mt-1 h-12" />
              <p className="text-sm font-semibold text-slate-600 mt-4 mb-2">Revenir quand ? <span className="text-slate-400 font-normal">(facultatif, +0,25 pt si daté)</span></p>
              <DateQuickPick value={nextDate} onChange={setNextDate} testidPrefix="feedback-date" />
            </div>
          )}

          {selected.extra === "date" && (
            <div>
              <p className="text-sm font-semibold text-slate-600 mb-2">
                Prochaine date de récupération {dateRequired && <span className="text-red-600">*</span>}
              </p>
              <DateQuickPick value={nextDate} onChange={setNextDate} testidPrefix="feedback-date" />
            </div>
          )}

          {selected.extra === "comment" && (
            <div>
              <label className="text-sm font-semibold text-slate-600">Commentaire (facultatif)</label>
              <Textarea data-testid="feedback-comment" autoFocus value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Précisez si besoin…" className="mt-1" />
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button data-testid="feedback-cancel" onClick={() => { setSelected(null); setNextDate(""); setContact(""); setComment(""); }}
              className="flex-1 h-12 rounded-2xl border-2 border-slate-200 text-slate-600 font-bold active:scale-[0.98] transition-transform">Retour</button>
            <button data-testid="feedback-confirm" disabled={saving || (dateRequired && !nextDate)} onClick={() => submit(selected)}
              className="flex-1 h-12 rounded-2xl bg-accent-600 text-white font-bold flex items-center justify-center active:scale-[0.98] transition-transform disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Valider
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
